import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckSquare, Square, Navigation, Plus, FileText, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';

const TechWorkOrder = () => {
  const navigate = useNavigate();
  const { woId } = useParams();
  const { toast } = useToast();
  const techName = localStorage.getItem('technicianName') || '';

  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);

  const loadWO = async () => {
    setLoading(true);
    try {
      const result = await crmBackend.getWorkOrderDetail(woId);
      setWo(result.data);
    } catch (err) {
      toast({ title: 'Could not load work order', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWO(); }, [woId]);

  const toggleItem = async (idx, currentValue) => {
    try {
      await crmBackend.updateWorkOrderItem(woId, idx, !currentValue, techName);
      setWo(prev => ({
        ...prev,
        items: prev.items.map(it =>
          it.idx === idx ? { ...it, completed: !currentValue, completed_by: techName } : it
        ),
      }));
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleGenerateInvoice = async () => {
    setGenerating(true);
    try {
      const result = await crmBackend.generateWOInvoice(woId, techName);
      toast({
        title: result.invoice ? `Invoice created: ${result.invoice}` : 'Job completed',
        description: result.invoice ? 'Items auto-completed and invoice generated.' : 'All items marked complete.',
      });
      await loadWO();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateSupplemental = async () => {
    try {
      const result = await crmBackend.createSupplementalEstimate(woId);
      toast({ title: 'Supplemental estimate created', description: result.supplemental_estimate });
      navigate(`/tech/job/${result.supplemental_estimate}`);
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Loading…</p>
    </div>
  );

  if (!wo) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Work order not found.</p>
    </div>
  );

  const done = wo.items.filter(it => it.completed).length;
  const total = wo.items.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const isInvoiced = wo.status === 'Invoiced';

  return (
    <div className="min-h-screen bg-gray-50 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
      {/* Header */}
      <div className="bg-green-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)}
            className="flex items-center space-x-2 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <span className="font-bold">{woId}</span>
          <button onClick={() => { setChatText(''); setChatOpen(true); }}
            className="hover:bg-green-600 p-2 rounded-lg transition-colors">
            <MessageCircle className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Info card */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <p className="font-semibold text-gray-900 text-lg">{wo.customer}</p>
                {wo.service_address && (
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm text-gray-500">{wo.service_address.split('\n')[0]}</p>
                    <a
                      href={`https://waze.com/ul?q=${encodeURIComponent(wo.service_address)}&navigate=yes`}
                      target="_blank" rel="noreferrer"
                      className="text-blue-600 text-xs flex items-center gap-1"
                    >
                      <Navigation className="w-3 h-3" /> Waze
                    </a>
                  </div>
                )}
                {wo.scheduled_date && (
                  <p className="text-xs text-gray-400 mt-1">Scheduled: {wo.scheduled_date}</p>
                )}
              </div>
              <Badge className={isInvoiced ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                {wo.status}
              </Badge>
            </div>
            {/* Progress bar */}
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{done}/{total} items complete</span>
                <span>{pct}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            {wo.invoice && (
              <p className="text-xs text-green-700 font-medium">Invoice: {wo.invoice}</p>
            )}
          </CardContent>
        </Card>

        {/* Additional Service Locations — inherited from the Estimate at WO
            creation time (frozen snapshot, same as the primary address
            above; never re-editable here, matching the established
            "frozen once copied" design for Work Orders). Read-only display
            only — the gap this closes: the data has reached the Work Order
            correctly since Phase 7, but nothing on this screen showed it. */}
        {wo.additional_service_locations?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Additional Service Locations ({wo.additional_service_locations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              {wo.additional_service_locations.map((loc) => (
                <div key={loc.service_location} className="bg-gray-50 rounded-md px-3 py-2">
                  <p className="text-sm font-medium text-gray-900">{loc.location_name}</p>
                  <p className="text-xs text-gray-500">
                    {[loc.address_line1, loc.address_line2, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Items checklist */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Items ({total})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {wo.items.map((item) => (
              <button
                key={item.idx}
                onClick={() => !isInvoiced && toggleItem(item.idx, item.completed)}
                disabled={isInvoiced}
                className={`w-full flex items-start gap-3 px-4 py-3 border-b last:border-0 text-left transition-colors
                  ${item.completed ? 'bg-green-50' : 'hover:bg-gray-50'}
                  ${isInvoiced ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {item.completed
                  ? <CheckSquare className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  : <Square className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {item.item_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.zone ? `Zone: ${item.zone} · ` : ''}Qty {item.qty} × ${Number(item.rate).toFixed(2)}
                    {item.source !== wo.source_estimate && (
                      <span className="ml-2 text-purple-600 font-medium">Supplemental</span>
                    )}
                  </p>
                  {item.completed && item.completed_by && (
                    <p className="text-xs text-green-600">✓ {item.completed_by}</p>
                  )}
                </div>
                <p className="text-sm font-semibold text-gray-900 shrink-0">
                  ${Number(item.amount).toFixed(2)}
                </p>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Supplemental estimates */}
        {wo.supplemental_estimates?.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-sm">Supplemental Estimates</CardTitle></CardHeader>
            <CardContent className="p-3 space-y-2">
              {wo.supplemental_estimates.map(est => (
                <div key={est} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{est}</span>
                  <Button size="sm" variant="outline" className="text-xs"
                    onClick={() => crmBackend.addSupplementalToWO(woId, est).then(loadWO)
                      .catch(e => toast({ title: 'Failed', description: e.message, variant: 'destructive' }))}>
                    Pull into WO
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom action buttons */}
      {!isInvoiced && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] space-y-2">
          <Button variant="outline" className="w-full h-10 border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={handleCreateSupplemental}>
            <Plus className="w-4 h-4 mr-2" /> Create Supplemental Estimate
          </Button>
          <Button
            onClick={handleGenerateInvoice}
            disabled={generating}
            className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-semibold"
          >
            <FileText className="w-5 h-5 mr-2" />
            {generating ? 'Generating…' : 'Generate Invoice'}
          </Button>
        </div>
      )}

      {/* Chat with office */}
      {chatOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 space-y-3">
            <h3 className="font-bold text-gray-900">Message Office</h3>
            <p className="text-xs text-gray-500">WO: {woId}</p>
            <textarea className="w-full border rounded-md p-2 text-sm resize-none" rows={4}
              placeholder="e.g. Supplemental estimate needs price approval…"
              value={chatText} onChange={e => setChatText(e.target.value)} />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setChatOpen(false)}>Cancel</Button>
              <Button disabled={chatSending || !chatText.trim()} className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={async () => {
                  setChatSending(true);
                  try {
                    await crmBackend.sendChatMessage(chatText.trim(), techName, woId);
                    toast({ title: 'Message sent' });
                    setChatOpen(false);
                  } catch (err) {
                    toast({ title: 'Send failed', description: err.message, variant: 'destructive' });
                  } finally {
                    setChatSending(false);
                  }
                }}>
                <MessageCircle className="w-4 h-4 mr-1" />
                {chatSending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechWorkOrder;
